import { useEffect, useRef } from "react";

import { Box, Button, Typography } from "@mui/material";

import { History } from "@mui/icons-material";

import useConversation from "@/hooks/useConversation";

import ChatBubble from "./elements/ChatBubble";
import MessageInput from "./MessageInput";
import { SidebarState } from "./Sidebar";

interface Props {
  sidebarState: SidebarState;
  isVisible: boolean;
  isSandbox?: boolean;
}

const FAKE_MESSAGES: any[] = [
  {
    sid: "fake-1",
    author: "tutor",
    body: "Hi Sarah! Are you ready to work on some algebra today?",
    dateCreated: new Date(Date.now() - 600000)
  },
  {
    sid: "fake-2",
    author: "student",
    body: "Yes! But I'm still a bit confused about solving for x in quadratic equations.",
    dateCreated: new Date(Date.now() - 500000)
  },
  {
    sid: "fake-3",
    author: "tutor",
    body: "No problem at all! That's exactly what we'll cover today. Do you want to try the first problem from your homework, or should I generate one for you?",
    dateCreated: new Date(Date.now() - 400000)
  },
  {
    sid: "fake-4",
    author: "student",
    body: "Can you generate one for me please?",
    dateCreated: new Date(Date.now() - 300000)
  },
  {
    sid: "fake-5",
    author: "tutor",
    body: "Sure thing! I've just added a new problem to the whiteboard.",
    dateCreated: new Date(Date.now() - 200000)
  },
  {
    sid: "fake-6",
    author: "student",
    body: "Thanks! So for this one, do I need to factor it first?",
    dateCreated: new Date(Date.now() - 100000)
  },
  {
    sid: "fake-7",
    author: "tutor",
    body: "Exactly! Look for two numbers that multiply to the constant term (c) and add to the middle term's coefficient (b).",
    dateCreated: new Date()
  },
];

function ChatPane({ sidebarState, isVisible, isSandbox }: Props) {
  const {
    messages,
    isFetchingPrevPage,
    getPrevPage,
    identity,
    handleReadMessages,
  } = useConversation();

  const messageListRef = useRef<HTMLDivElement | null>(null);

  // Scroll to the bottom of the message list
  useEffect(() => {
    if (messageListRef.current && isVisible) {
      // Small timeout to ensure content is rendered
      const timer = setTimeout(() => {
        if (messageListRef.current) {
          messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages, isVisible, isSandbox]);

  useEffect(() => {
    if (sidebarState === SidebarState.STANDARD && messages) {
      handleReadMessages();
    }
  }, [handleReadMessages, messages, sidebarState]);

  const handleLoadMore = async () => {
    if (messageListRef.current) {
      const currentScrollHeight = messageListRef.current.scrollHeight;
      await getPrevPage();
      messageListRef.current.scrollTop =
        messageListRef.current.scrollHeight - currentScrollHeight;
    }
  };

  return (
    <Box
      sx={{
        display: isVisible ? "flex" : "none",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        ref={messageListRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
          overflowY: "auto",
          px: 1.5,
          py: 2,
        }}
      >
        {isFetchingPrevPage && (
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 14,
              textAlign: "center",
            }}
          >
            Loading...
          </Typography>
        )}
        {messages?.hasPrevPage && !isFetchingPrevPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              onClick={handleLoadMore}
              startIcon={<History sx={{ fontSize: 16 }} />}
              sx={{
                color: "text.secondary",
                fontSize: 14,
                textTransform: "none",
              }}
            >
              Load more
            </Button>
          </Box>
        )}
        {(isSandbox ? FAKE_MESSAGES : messages?.items || []).map((message) => {
          return (
            <ChatBubble
              key={message.sid}
              message={message}
              isOwnMessage={isSandbox ? message.author === "tutor" : message.author === identity}
            />
          );
        })}
      </Box>
      <Box sx={{ px: 1.5 }}>
        <MessageInput disabled={isSandbox} />
      </Box>
    </Box>
  );
}

export default ChatPane;
